/* THE JOIN — where `CoachProgression`'s twin meets `V6Progression`'s.
 *
 * 🔴 WHY A THIRD FILE RATHER THAN A FUNCTION IN `progression.ts`. That file's header states that
 * the rules are deliberately NOT transcribed there and declares `RuleEngine` as a seam with no
 * implementation, so that a caller who wants verdicts must supply one. A hard import of
 * `v6rules.ts` into it would contradict the design it documents. The join is a real thing with a
 * real name, and it lives here: `progression.ts` keeps its independence, `v6rules.ts` keeps
 * knowing nothing about coach rows, and everything that has to know BOTH vocabularies is in one
 * file a reader can hold in their head.
 *
 * WHAT THE SWIFT DOES, so the shape is not mistaken for an invention: `CoachProgression.plan`
 * (CoachProgression.swift:798) builds `V6Progression.Row`s out of its own `Unit`s, calls
 * `V6Progression.nextWeek`, and maps the result back through `redistribute`. That crossing is
 * what this file transcribes, function by function, in the order `plan` performs it.
 */

import {
  type LoggedSession, type LoggedSet, type Outcome, type PriorSession, type Row, type SetRow,
  type Unit,
  type Verdict,
  blockName, cleanStreak, consecutiveMisses, dateText, doseMoved, doseSignature, isRampOnly,
  isRampSet, isReconstructed, loadTarget, pair, perSetLoads, redistribute, ruledSets, unitKey, units, workingSets,
} from "./progression.ts";
import {
  type Json, type RuleRow, type RuleRowPrescription,
  ProgramConfig, fromCoachLoggedSet, nextWeek, performance,
} from "./v6rules.ts";
import {
  type LadderChain, type LadderLogged, type LadderTarget,
  deletedSentence, endSentence, ladderChain, ladderIsDue, moveSentence, notYetSentence, unreadableSentence,
} from "./coachLadder.ts";
import {
  type RowEdit, type RowFacts, type TabSetFacts, type TabSteps, type TabLive, type TabWeek,
  NO_ROW_FACTS, applyChange, changedSentence, isTabs, judgedByLog, liveStep, pausedSentence, resumedSentence, rowEdit,
  splitSentence, tabNotYetSentence, tabSteps, targetClause, touchesSets,
} from "./coachTabs.ts";

/** Mirrors `CoachProgression.Day`, CoachProgression.swift:122.
 *  ⚠️ `date` IS AN ISO STRING, not a `Date` — the same decision `progression.ts` states in its
 *  header, so two implementations do not depend on two timezone databases. */
export interface Day {
  assignmentId: string;
  date: string;
  /** `coalesce(root_workout_id, workout_id)` — the identity that survives copy-on-assign, and the
   *  same key `coach_assignments_one_source_per_day_idx` is built on. */
  rootWorkoutId: string;
  rows: Row[];
}

/** Mirrors `CoachProgression.PriorDay`, CoachProgression.swift:746. */
export interface PriorDay {
  day: Day;
  /** Nil is not a failure: it FREEZES. */
  logged: LoggedSession | null;
}

/** Mirrors `CoachProgression.prescription(for:sessionId:state:)`, CoachProgression.swift:491 —
 *  the SYNTHETIC prescription a rule is handed for one unit.
 *
 *  🔴 IT IS BUILT FROM `ruledSets`, NOT FROM EVERY SET. A coach puts the ramp wherever he likes —
 *  four of his own v8 rows carry it INSIDE the working row as `warm_up`-typed sets — and the phone
 *  logs those with status `warmup`, which `pair` drops. Counting every set made a row shaped
 *  2 ramp + 3 working read "only 3 of 5 sets were completed" with every set done exactly as asked;
 *  `consecutive_fails` counted one, and the second such week opened §5.1's load hold. His Seated
 *  Barbell Press and Back Squat, MEASURED live. A rule sees WORKING sets and nothing else.
 *
 *  ⚠️ THE DERIVED STATE IS MERGED **OVER** THE AUTHORED RULE, never the other way round: the
 *  library states the rule and the athlete's own history states where they are in it. Reversing
 *  the merge would let a stale counter overwrite a rule the coach had just rewritten.
 *
 *  ⚠️ AND THE PER-SET ARRAYS ARE CARRIED **ONLY WHERE THE SETS REALLY DIFFER**. An array as long
 *  as `sets` whose values are all equal is the same prescription written twice, and §5.3's
 *  `syncRepRange` would then fight the range it was derived from.
 *
 *  ⚠️ FIELDS THE SWIFT SETS THAT THIS TWIN HAS NO HOME FOR, named so the omission reads as
 *  deliberate: `blockName`/`position`/`sectionType`/`sectionFormat`/`restSeconds`/`tempo` are
 *  `ProgramSessionExercise` columns that NO PORTED RULE READS — the section role reaches the
 *  engine on `RuleRow.sectionRole` instead, which is where every §5 rule actually asks for it.
 *  `blockName` is still called below so the mapping is exercised and cannot rot. */
export function prescriptionFor(
  unit: Unit,
  sessionId: string,
  state: Record<string, Json>,
): RuleRowPrescription {
  const core = ruledSets(unit);
  const reps = core.map((s) => s.reps).filter((r): r is number => r !== null);
  const seconds = core.map((s) => s.timeSeconds).filter((r): r is number => r !== null);
  const perSetWeights = perSetLoads(core);
  const uniformReps = new Set(reps).size <= 1;

  const authored = (unit.rule !== null && typeof unit.rule === "object" && !Array.isArray(unit.rule))
    ? { ...(unit.rule as Record<string, Json>) }
    : {};
  const config: Record<string, Json> = { ...authored };
  /* F15 step 3.8 — §5.5's promotion `gate` is NOT handed on (`gateSentence`; Swift: the same line). */
  delete config["gate"];
  for (const [key, value] of Object.entries(state)) config[key] = value;

  /* Exercised so the role→block mapping cannot rot unused. ⚠️ `coach_workout_sections
     .section_type` has THREE values and `BlockName` has many, so it is a MAPPING and never a
     cast; what matters to the rules is that a warm-up never reads as main. */
  void blockName(unit.sectionRole);

  return {
    id: unit.rowIds[0] ?? "",
    programSessionId: sessionId,
    exerciseId: unit.libraryExerciseId,
    sets: core.length,
    repsMin: reps.length > 0 ? Math.min(...reps) : null,
    repsMax: reps.length > 0 ? Math.max(...reps) : null,
    /* §5.3's ladder and F6's per-set weights are carried ONLY where the sets really do differ. */
    perSetReps: (!uniformReps && reps.length === core.length) ? reps : [],
    holdSecondsMin: seconds.length > 0 ? Math.min(...seconds) : null,
    holdSecondsMax: seconds.length > 0 ? Math.max(...seconds) : null,
    targetWeightKg: loadTarget(core),
    perSetWeightKg: perSetWeights,
    perSetSeconds: [],
    perSetRestSeconds: [],
    perSetTypes: [],
    assistKg: null,
    progressionConfig: Object.keys(config).length === 0 ? null : config,
    progressionRule: "",
    selectionReason: "",
    formatGroupId: null,
    notes: "",
  };
}

/** The working-dose address a unit is looked up by across weeks. Re-exported here so the join's
 *  own callers do not have to know it lives in `progression.ts`. */
export const workingDose = (u: Unit): string => doseSignature(ruledSets(u));

/** Whether two units are the same PRESCRIPTION a week apart when their dose has legitimately
 *  moved — which is what a progression does. Mirrors the fallback in
 *  `CoachProgression.priorSessions`, CoachProgression.swift:968. */
export const sameKind = (a: Unit, b: Unit): boolean => isRampOnly(a) === isRampOnly(b);

/** Re-exported for the tests and for `priorSessions`, which needs the same set list. */
export const ruledSetsOf = (u: Unit): SetRow[] => ruledSets(u);

/** Mirrors `CoachProgression.priorSessions(for:in:)`, CoachProgression.swift:963 — a unit's
 *  earlier appearances, MOST RECENT FIRST, each with the verdict the rule engine itself would
 *  give it.
 *
 *  ⚠️ IT ASKS `performance` RATHER THAN RE-DERIVING "was it clean". That is what keeps ONE
 *  definition of a completed set across the whole system; a second one here would be the
 *  twenty-fourth instance of this repository's defining failure, inside the file written to
 *  prevent it.
 *
 *  🔴 BOUND BY (LIBRARY EXERCISE, SECTION ROLE, WORKING DOSE), FALLING BACK TO THE SAME-ROLE UNIT
 *  OF THE SAME KIND — AND THE BINDING IS THE WHOLE POINT OF THIS FUNCTION. It used to bind by
 *  LIBRARY ID ALONE, taking the first unit in day order, so his Back Squat's MAIN unit read its
 *  WARM-UP unit's history: every log there is `warmup`, so `pair` drops it, the verdict was nil,
 *  the dose differed and every counter read zero. §5.1's two-miss hold was UNREACHABLE for any
 *  exercise that also appears in the warm-up, and the two-weekly step's counter could never reach
 *  two. (The 2026-09-10 review, mechanism E.)
 *
 *  ⚠️ THE EXACT WORKING DOSE IS TRIED FIRST because it is the address `units` itself uses. When
 *  the dose has legitimately MOVED — which is what a progression does — the same-role unit of the
 *  same KIND (working, or ramp-only matching this one) is the same prescription a week older.
 *  Without that second reach every load step would erase its own history.
 *
 *  ⚠️ AND THREE DIFFERENT SILENCES ALL RETURN A SESSION WITH A **NULL** VERDICT rather than no
 *  session at all: a day with no log, a RECONSTRUCTED log (read as untrained), and a log that does
 *  not line up with what was assigned. They are not failures, and `cleanStreak` treats a null as a
 *  PAUSE rather than a break — so dropping the entry would shorten a streak the athlete earned.
 *  Only a day where the unit does not appear at all returns nothing. */
export function priorSessions(unit: Unit, history: PriorDay[]): PriorSession[] {
  const wanted = workingDose(unit);
  const out: PriorSession[] = [];

  for (const prior of history) {
    const candidates = units(prior.day.rows).filter((u) =>
      u.libraryExerciseId === unit.libraryExerciseId && u.sectionRole === unit.sectionRole);
    const match = candidates.find((u) => workingDose(u) === wanted)
      ?? candidates.find((u) => sameKind(u, unit));
    if (match === undefined) continue;

    const dose = workingDose(match);
    const logged = prior.logged;
    if (logged === null || isReconstructed(logged)) {
      out.push({ date: prior.day.date, doseSignature: dose, verdict: null });
      continue;
    }
    const paired = pair(prior.day.rows, logged);
    if (paired === null) {
      out.push({ date: prior.day.date, doseSignature: dose, verdict: null });
      continue;
    }
    const sets = match.rowIds.flatMap((id) => paired.get(id) ?? []);
    if (sets.length === 0) {
      out.push({ date: prior.day.date, doseSignature: dose, verdict: null });
      continue;
    }

    const prescription = prescriptionFor(match, prior.day.assignmentId, {});
    /* ⚠️ `fromCoachLoggedSet` IS ASKED RATHER THAN THE FIELDS COPIED. The coach set carries a
       STATUS string and the engine set carries a `completed` BOOLEAN, and that adapter is the one
       place this repository lets the two vocabularies meet — its own comment says so. Copying the
       fields here would put the status→boolean rule in a second place, which is the failure the
       adapter exists to prevent. */
    const log = { sets: sets.map(fromCoachLoggedSet) };
    /* ⚠️ `notLogged` BECOMES A NULL VERDICT, NOT A MISS. A week nobody trained is not a failed
       session — the three-state finding, and the reason `Performance` has three cases at all. */
    const kind = performance(prescription, log).kind;
    const verdict: Verdict = kind === "clean" ? "clean" : (kind === "missed" ? "missed" : null);
    out.push({ date: prior.day.date, doseSignature: dose, verdict });
  }
  return out;
}

/* ── THE PAIN VOCABULARY, WHICH IS A THIRD ONE ────────────────────────────────────────────────
 *
 * `coach_exercises.primary_muscles` speaks `CoachMuscleGroup` — SEVENTEEN values — while the
 * weekly check-in reports pain in the six the engine uses, and the injuries screen speaks a third,
 * unrelated, eighteen-item Title-Case list. §5.12's freeze is keyed on the CHECK-IN's vocabulary,
 * so a coach row has to be translated into it or the freeze silently never fires for a coached
 * athlete. Mirrors `CoachProgression.painRegionByCoachMuscle`, CoachProgression.swift:710.
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

/** The six the engine and the check-in share. Mirrors `MuscleNormalizer.Group`,
 *  MuscleNormalizer.swift:63. ⚠️ `arms` is the one `v6rules.WRIST_REGION` names. */
export const PAIN_REGIONS = ["legs", "back", "chest", "shoulders", "arms", "core"] as const;

/** Mirrors `CoachProgression.painRegionByCoachMuscle`, CoachProgression.swift:710.
 *
 *  ⚠️ A DECLARED TABLE RATHER THAN A SWITCH, AND THE SWIFT'S REASON IS A COMPILE ONE: the enum
 *  lives in `CoachExerciseFields.swift`, which reaches a SwiftUI file the harness cannot compile,
 *  so the exhaustive `switch` that would make a new group a COMPILER error is unavailable there.
 *  Its replacement is `coach-progression-1` ruler 1, which parses the enum's own case list out of
 *  that file and requires this table to name every one. **The TypeScript twin has the same hole
 *  and takes the same remedy** — `coachPlanPainRegions.test.ts` parses the same Swift file — so a
 *  seventeenth-plus group turns BOTH sides red rather than falling silently into "trains
 *  nothing", which is the shape that makes a safety rule stop firing with nothing red.
 *
 *  ⚠️ `fullBody` AND `upperBody` MAP TO SEVERAL REGIONS ON PURPOSE: a row that trains everything
 *  is frozen by pain anywhere in it. */
export const PAIN_REGION_BY_COACH_MUSCLE: Record<string, readonly string[]> = {
  quads:         ["legs"],
  hamstrings:    ["legs"],
  glutes:        ["legs"],
  hipGroin:      ["legs"],
  lowerLeg:      ["legs"],
  lowerBody:     ["legs"],
  midBack:       ["back"],
  upperBackNeck: ["back"],
  lowerBack:     ["back"],
  chest:         ["chest"],
  shoulders:     ["shoulders"],
  biceps:        ["arms"],
  triceps:       ["arms"],
  forearms:      ["arms"],
  core:          ["core"],
  upperBody:     ["back", "chest", "shoulders", "arms"],
  fullBody:      [...PAIN_REGIONS],
};

/** Mirrors `CoachProgression.painRegions(for:)`, CoachProgression.swift:733.
 *
 *  ⚠️ AN UNRECOGNISED MUSCLE STRING CONTRIBUTES **NOTHING** AND IS NOT AN ERROR HERE — the
 *  completeness ruler is what makes the table complete, and guessing a region from a word nobody
 *  declared is how a freeze fires on the wrong exercise.
 *
 *  ⚠️ SORTED, because the result reaches a SENTENCE the coach reads (§5.12's freeze names the
 *  regions) and an unordered set would make the same week produce two different strings. */
export function painRegions(muscles: string[]): string[] {
  const out = new Set<string>();
  for (const raw of muscles) {
    for (const region of PAIN_REGION_BY_COACH_MUSCLE[raw] ?? []) out.add(region);
  }
  return [...out].sort();
}

/** Mirrors `CoachProgression.configFaults`, CoachProgression.swift:1065 — rules whose CONFIG is
 *  incomplete in a way that fails SILENTLY AND LATE.
 *
 *  🔴 THE ONE THIS EXISTS FOR COST THREE WEEKS OF SILENCE. `distance_cm` is two-stage: the reps
 *  climb to `rep_target` at the current distance, and only then does the distance step and the
 *  reps go back to `rep_start`. The reset reads `config.int("rep_start") ?? next.repsMin ?? target`
 *  — and `repsMin` is DERIVED FROM THE CURRENT WEEK'S SETS, so by the time the reset is reached
 *  the sets are already AT the target and `repsMin` IS the target. Omit `rep_start` and the reps
 *  "reset" to where they already are, after which THE DISTANCE STEPS EVERY WEEK FOR EVER with the
 *  reps pinned. ⚠️ The fallback is CORRECT for a row that never had a target; it is a trap for one
 *  that does, which is why this is a check and not a change to the fallback.
 *
 *  The v6 seed authors both keys together. The coach-side config carried only the first, because
 *  it was written from a rule stated in PROSE rather than from the seed that implements it — which
 *  is why this is a standing check and not a one-off repair.
 *
 *  ⚠️ MAIN ROWS ONLY, AND ONCE PER LIBRARY EXERCISE. The same library row can appear twice in a
 *  day (his Back Squat is in the warm-up and in main), and a coach does not need the same sentence
 *  twice about one exercise. */
export function configFaults(rows: Row[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    if (row.sectionRole !== "main") continue;
    if (seen.has(row.libraryExerciseId)) continue;
    seen.add(row.libraryExerciseId);
    const rule = row.rule;
    if (rule === null || typeof rule !== "object" || Array.isArray(rule)) continue;
    const config = rule as Record<string, unknown>;
    /* ⚠️ PRESENCE, NOT TRUTHINESS. `rep_start: 0` is an ANSWER — the reps go back to zero — and a
       truthy test would call that row faulty while letting `rep_target: 0` through. The Swift
       compares against nil for the same reason. */
    if (config["rep_target"] !== undefined && config["rep_start"] === undefined) {
      out.push(`${row.name}: its rule declares a rep target but never says what the reps go back `
        + "to. The reset silently becomes a no-op and the distance then steps every week with the "
        + "reps pinned at the target. Add `rep_start`.");
    }
  }
  return out;
}

/* ── THE ORDERING RULE (his catch, 2026-08-31) ────────────────────────────────────────────────
 * Mirrors `CoachProgression.OrderFault` and `orderFaults`, CoachProgression.swift:1004 and :1035.
 * ──────────────────────────────────────────────────────────────────────────────────────────── */

/** One place in a day where a quality-limited movement sits behind a load-limited one of the same
 *  pattern. Mirrors `CoachProgression.OrderFault`, CoachProgression.swift:1004. */
export interface OrderFault {
  qualityLimited: string;
  behind: string;
  pattern: string;
}

/** The sentence the coach reads. Transcribed exactly — it is part of the verdict the parity
 *  fixture compares, not a paraphrase. */
export const orderFaultMessage = (f: OrderFault): string =>
  `${f.qualityLimited} is ordered after ${f.behind}, which loads the same ${f.pattern} pattern. `
  + "A quality-limited movement is progressed by holding a SHAPE, so fatigue does not make it "
  + "harder — it makes it a different exercise, and the set still reads as completed. Put it "
  + "first.";

/** Mirrors `CoachProgression.orderFaults`, CoachProgression.swift:1035.
 *
 *  ⚠️ **LOAD-LIMITED IS READ OFF THE PRESCRIPTION, NOT OFF A SECOND FLAG.** A row is load-limited
 *  when its own sets NAME A WEIGHT: that is the property the rule is actually about ("progressed
 *  by adding weight, tolerates pre-fatigue"), it needs nothing authored, and it cannot drift from
 *  what the athlete is asked to lift.
 *
 *  🔴 **AND THE RULE IS DELIBERATELY NARROW — IT CATCHES ONE OF THE THREE EDITS IT SHIPPED WITH,
 *  WHICH IS STATED RATHER THAN PAPERED OVER.** It flags Monday (a banded front-lever pull behind
 *  4 × 4 @ 30 kg). It does NOT flag Friday, because `Ring Muscle-Up` names no weight, nor
 *  Wednesday, whose prone hip extension is a ZONE-COVERAGE argument and not a quality one.
 *  Widening it to "behind any high-fatigue row of the same pattern" would flag THURSDAY's
 *  `Tuck Planche` behind `Deep Chest-to-Wall HSPU` — structurally the same shape as Friday — and
 *  the owner has ruled Thursday out on the grounds that it is hypertrophy VOLUME and
 *  order-insensitive there. **No principled line separates Friday from Thursday**, so the check
 *  that cannot produce a false positive ships and the other two edits are authored. A lint that
 *  flagged a day he had excluded would be one he learned to ignore.
 *
 *  🔴 NIL `isQualityLimited` IS UNCLASSIFIED AND NEVER FLAGS: "guessing from a name is what put a
 *  tuck lever on a bar". The test is `=== true`, so absent, null and false all stay silent. */
export function orderFaults(rows: Row[]): OrderFault[] {
  const main = rows.filter((r) => r.sectionRole === "main")
                   .slice()
                   .sort((a, b) => a.ordinal - b.ordinal);
  const loadLimited = (r: Row): boolean => r.sets.some((s) => (s.weightKg ?? 0) > 0);
  const out: OrderFault[] = [];
  for (let index = 0; index < main.length; index++) {
    const row = main[index]!;
    if (row.isQualityLimited !== true) continue;
    const pattern = row.movementPattern;
    if (pattern === null || pattern === undefined) continue;
    for (const earlier of main.slice(0, index)) {
      if (earlier.movementPattern !== pattern) continue;
      if (!loadLimited(earlier)) continue;
      /* ⚠️ `!== true`, NOT `=== false`: an UNCLASSIFIED row earlier in the day may still be the
         thing a quality-limited one sits behind. Only a row explicitly marked quality-limited is
         excused from being the cause. */
      if (earlier.isQualityLimited === true) continue;
      out.push({ qualityLimited: row.name, behind: earlier.name, pattern });
    }
  }
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * `plan()` — THE ORCHESTRATOR THE PARITY FIXTURE PINS.
 * Mirrors `CoachProgression.plan`, CoachProgression.swift:798, in the order it performs it:
 *   the log and the one reason it may be discarded → the engine's own inputs → the rules →
 *   the reasons and warnings → redistribute, unit by unit → the verdict.
 * ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** Mirrors `CoachProgression.UnitPlan`, CoachProgression.swift:776. */
export interface UnitPlan { unit: Unit; outcome: Outcome }

/** Mirrors `CoachProgression.Plan`, CoachProgression.swift:781. */
export interface Plan {
  assignmentId: string;
  plans: UnitPlan[];
  /** The rules' own sentences, in the order the athlete should read them. */
  reasons: string[];
  warnings: string[];
  /** ⚠️ NAMED RATHER THAN INFERRED FROM AN EMPTY CHANGE LIST: "nothing to progress from" and
   *  "everything was already right" are different answers and want different words. */
  frozenForWantOfALog: boolean;
  /** `plans.filter { .changed }.count`, as the Swift's computed property. */
  changedUnits: number;
  /** F15 step 3.3 — mirrors `Plan.isDeloadWeek`: the week is written as a deload (`deloadDose` on every unit). */
  isDeloadWeek: boolean;
  /** F15 step 3.3 — mirrors `Plan.proposals`: §5.12's stops in the coach's words. Proposals, never applied. */
  proposals: string[];
  /** F15 step 3.3 — mirrors `Plan.pausedForPain`: the main, ruled units held for a pain report. */
  pausedForPain: string[];
  /** F15 step 3.4 — mirrors `Plan.moves`: the ladder moves this week earned (the unit's `changed` sets are the next
   *  exercise's start). Empty on a deload week. */
  moves: LadderMove[];
  /** F15 step 3.4 — mirrors `Plan.ladderSaid`: every move first, then each chain that ended. Empty on a deload week. */
  ladderSaid: string[];
  /** F15 4.1b — mirrors `Plan.rowEdits`: the row edits this week's tabs earned (tempo · note · each side · hidden), by
   *  `unitKey`. Absent = none (a plan built before 4.1b). */
  rowEdits?: RowEditAt[];
}

/** Mirrors `CoachProgression.RowEditAt` (F15 4.1b). */
export interface RowEditAt { unitKey: string; edit: RowEdit }

/** Mirrors `CoachProgression.LadderMove` — the unit it applies to (`unitKey`, the address that survives into the clone) and
 *  the next exercise's facts. (The Swift's `Substitution` carries the same two names; `target.name` is its `toName`.) */
export interface LadderMove { unitKey: string; target: LadderTarget }

/* ══ F15 STEP 3.4 — A LADDER UNIT (mirrors `CoachProgression.ladderStart` … `ladderOutcome`) ═══════════════════════════════ */

/** Mirrors `ladderStart` — the same sets in the same places (a split superset stays split), each at the next exercise's
 *  first number (the row's own when the library states none), the kilos BLANK. Positions are kept as they are. */
export function ladderStart(sets: SetRow[], target: LadderTarget): SetRow[] {
  return sets.map((set) => {
    const s: SetRow = { ...set, weightKg: null };
    if (target.isHold) {
      s.timeSeconds = target.holdSecondsMin ?? set.timeSeconds ?? set.reps;
      s.reps = null;
    } else {
      s.reps = target.repsMin ?? set.reps;
      s.timeSeconds = null;
    }
    return s;
  });
}

/** Mirrors `doseWords` — "3 × 5" / "3 × 20 s"; null when the first working set names no number. */
export function doseWords(sets: SetRow[], isHold: boolean): string | null {
  const working = sets.filter((s) => !isRampSet(s));
  const first = working[0];
  if (first === undefined) return null;
  const n = isHold ? (first.timeSeconds === null ? null : `${first.timeSeconds} s`)
                   : (first.reps === null ? null : String(first.reps));
  if (n === null) return null;
  return `${working.length} × ${n}`;
}

/** Mirrors `ladderLogged`. */
export const ladderLogged = (sets: LoggedSet[]): LadderLogged[] =>
  sets.map((s) => ({ reps: s.reps, seconds: s.holdSeconds, kg: s.weightKg, status: s.status }));

/** Mirrors `ladderHistory(for:in:)` — the same unit's earlier sessions, most recent first (null = nothing usable logged
 *  that week). Bound like `priorSessions`: same library exercise and role, the same working dose first, then the same kind
 *  of unit — so after a move the next exercise's own history starts EMPTY. A day without the unit contributes nothing. */
export function ladderHistory(unit: Unit, history: PriorDay[]): (LadderLogged[] | null)[] {
  const wanted = workingDose(unit);
  const out: (LadderLogged[] | null)[] = [];
  for (const prior of history) {
    const candidates = units(prior.day.rows).filter((u) =>
      u.libraryExerciseId === unit.libraryExerciseId && u.sectionRole === unit.sectionRole);
    const match = candidates.find((u) => workingDose(u) === wanted)
      ?? candidates.find((u) => isRampOnly(u) === isRampOnly(unit));
    if (match === undefined) continue;
    const logged = prior.logged;
    const paired = logged === null || isReconstructed(logged) ? null : pair(prior.day.rows, logged);
    if (paired === null) { out.push(null); continue; }
    const sets = match.rowIds.flatMap((id) => paired.get(id) ?? []);
    out.push(sets.length === 0 ? null : ladderLogged(sets));
  }
  return out;
}

/** Mirrors `ladderOutcome` — reached = measured from the logged sets (`ladderIsDue`); the chain's end, a deleted next
 *  exercise and an unreadable step each stop it and are SAID; a pain report in a region it trains holds it. */
export function ladderOutcome(unit: Unit, chain: LadderChain, current: LoggedSet[], input: PlanInput,
                              painful: string[]): { outcome: Outcome; move: LadderMove | null; said: string | null;
                                                    paused: boolean } {
  const link = chain.links[0];
  if (link === undefined) {
    const said = chain.brokenAt === null ? endSentence(unit.name) : unreadableSentence(unit.name);
    return { outcome: { kind: "unchanged", reason: said }, move: null, said, paused: false };
  }
  const target = (input.ladderTargets ?? {})[link.next];
  if (target === undefined) {
    const said = deletedSentence(unit.name);
    return { outcome: { kind: "unchanged", reason: said }, move: null, said, paused: false };
  }
  if (painRegions(input.musclesByExerciseId[unit.libraryExerciseId] ?? []).some((r) => painful.includes(r))) {
    return { outcome: { kind: "unchanged", reason: `${unit.name}: held for the pain reported.` }, move: null, said: null,
             paused: true };
  }
  if (current.length === 0) {
    return { outcome: { kind: "unchanged", reason: `${unit.name}: nothing logged — stays as written.` }, move: null,
             said: null, paused: false };
  }
  const sessions: (LadderLogged[] | null)[] = [ladderLogged(current), ...ladderHistory(unit, input.history)];
  if (!ladderIsDue(link, workingSets(unit).length, sessions)) {
    return { outcome: { kind: "unchanged", reason: notYetSentence(unit.name, link) }, move: null, said: null,
             paused: false };
  }
  const sets = ladderStart(unit.sets, target);
  const said = moveSentence(unit.name, link, target, doseWords(sets, target.isHold));
  const move: LadderMove = { unitKey: unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets), target };
  return { outcome: { kind: "changed", sets, reason: said }, move, said, paused: false };
}

/* ══ F15 STEP 4.1 — A TABS UNIT (mirrors `CoachProgression.TabsUnit` … `tabsOutcome`) ═══════════════════════════════════ */

/** Mirrors `CoachProgression.TabsUnit`. */
export interface TabsUnit { steps: TabSteps; live: TabLive }

/** Mirrors `rowFacts(_:in:)` (F15 4.1b) — the unit's row as written (its FIRST row), hidden only when every row of it is. */
export function rowFacts(unit: Unit, rows: Row[]): RowFacts {
  const mine = rows.filter((r) => unit.rowIds.includes(r.id)).sort((a, b) => a.ordinal - b.ordinal);
  const first = mine[0];
  if (first === undefined) return NO_ROW_FACTS;
  return { tempo: first.tempo ?? null, note: first.note ?? null, eachSide: first.eachSide ?? false,
           hidden: mine.every((r) => r.hidden === true) };
}

/** Mirrors `setFacts`. */
export const setFacts = (sets: SetRow[]): TabSetFacts[] =>
  sets.map((s) => ({ reps: s.reps, kg: s.weightKg, seconds: s.timeSeconds, rest: s.restSeconds,
                     type: s.setType as TabSetFacts["type"] }));

/** Mirrors `tabsWeeks(for:input:current:)` — OLDEST first, the current week last, bound like `ladderHistory`; a week
 *  without the unit is `present: false`. */
export function tabsWeeks(unit: Unit, input: PlanInput, current: LoggedSet[]): TabWeek[] {
  const wanted = workingDose(unit);
  const weeks: TabWeek[] = [...input.history].reverse().map((prior) => {
    const candidates = units(prior.day.rows).filter((u) =>
      u.libraryExerciseId === unit.libraryExerciseId && u.sectionRole === unit.sectionRole);
    const match = candidates.find((u) => workingDose(u) === wanted)
      ?? candidates.find((u) => isRampOnly(u) === isRampOnly(unit));
    if (match === undefined) return { present: false, hidden: false, logged: null, sets: [] };
    let logged: LadderLogged[] | null = null;
    const l = prior.logged;
    const paired = l === null || isReconstructed(l) ? null : pair(prior.day.rows, l);
    if (paired !== null) {
      const sets = match.rowIds.flatMap((id) => paired.get(id) ?? []);
      logged = sets.length === 0 ? null : ladderLogged(sets);
    }
    const row = rowFacts(match, prior.day.rows);
    return { present: true, hidden: row.hidden, logged, sets: setFacts(workingSets(match)), row };
  });
  const row = rowFacts(unit, input.day.rows);
  weeks.push({ present: true, hidden: row.hidden, logged: current.length === 0 ? null : ladderLogged(current),
               sets: setFacts(workingSets(unit)), row });
  return weeks;
}

/** Mirrors `tabsLive` — every tabs unit by `unitKey`, and the input with each STANDING tab's §5 rule handed to its rows
 *  unchanged (the migration's guarantee). */
export function tabsLive(input: PlanInput): { tabs: Map<string, TabsUnit>; input: PlanInput } {
  let paired = new Map<string, LoggedSet[]>();
  if (input.logged !== null && !isReconstructed(input.logged)) paired = pair(input.day.rows, input.logged) ?? paired;
  const tabs = new Map<string, TabsUnit>();
  const inner = new Map<string, unknown>();
  for (const unit of units(input.day.rows)) {
    const parsed = tabSteps(unit.rule);
    if (parsed === null) continue;
    const live = liveStep(parsed.steps, tabsWeeks(unit, input, unit.rowIds.flatMap((id) => paired.get(id) ?? [])));
    const step = parsed.steps[live.index];
    if (step !== undefined && step.action.kind === "rule") {
      for (const id of unit.rowIds) inner.set(id, step.action.rule);
      continue;
    }
    tabs.set(unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets), { steps: parsed, live });
  }
  if (inner.size === 0) return { tabs, input };
  const rows = input.day.rows.map((row) => (inner.has(row.id) ? { ...row, rule: inner.get(row.id) } : row));
  return { tabs, input: { ...input, day: { ...input.day, rows } } };
}

/** Mirrors `tabsOutcome` — in `ladderOutcome`'s order, so a migrated ladder answers word for word; a due step that changes
 *  the ROW (F15 4.1b) carries its `edit` beside the sets. */
export function tabsOutcome(unit: Unit, tabs: TabsUnit, current: LoggedSet[], input: PlanInput,
                            painful: string[]): { outcome: Outcome; move: LadderMove | null; said: string | null;
                                                  paused: boolean; edit: RowEdit | null } {
  const decided = tabsDecision(unit, tabs, current, input, painful);
  const step = tabs.steps.steps[tabs.live.index];
  if (!tabs.live.due || step === undefined || decided.outcome.kind !== "changed") return { ...decided, edit: null };
  return { ...decided, edit: rowEdit(step.action, rowFacts(unit, input.day.rows)) };
}

function tabsDecision(unit: Unit, tabs: TabsUnit, current: LoggedSet[], input: PlanInput,
                      painful: string[]): { outcome: Outcome; move: LadderMove | null; said: string | null;
                                            paused: boolean } {
  const unchanged = (reason: string, said: string | null, paused = false) =>
    ({ outcome: { kind: "unchanged" as const, reason }, move: null, said, paused });
  const step = tabs.steps.steps[tabs.live.index];
  if (step === undefined) {
    const said = tabs.steps.brokenAt === null ? endSentence(unit.name) : unreadableSentence(unit.name);
    return unchanged(said, said);
  }
  let target: LadderTarget | undefined;
  if (step.action.kind === "next") {
    target = (input.ladderTargets ?? {})[step.action.next];
    if (target === undefined) { const said = deletedSentence(unit.name); return unchanged(said, said); }
  }
  if (painRegions(input.musclesByExerciseId[unit.libraryExerciseId] ?? []).some((r) => painful.includes(r))) {
    return unchanged(`${unit.name}: held for the pain reported.`, null, true);
  }
  if (judgedByLog(step.target) && current.length === 0) {
    return unchanged(`${unit.name}: nothing logged — stays as written.`, null);
  }
  if (!tabs.live.due) return unchanged(tabNotYetSentence(unit.name, step, tabs.live.weeksOn), null);
  const action = step.action;
  if (action.kind === "next" && target !== undefined) {
    const sets = ladderStart(unit.sets, target);
    const dose = doseWords(sets, target.isHold);
    const said = step.target.kind === "numbers"
      ? moveSentence(unit.name, { next: target.id.toLowerCase(), reps: step.target.reps, kg: step.target.kg,
                                  seconds: step.target.seconds, sessions: step.target.sessions }, target, dose)
      : `${unit.name}: ${targetClause(step.target)} — next week ${target.name}${dose !== null ? `, ${dose}` : ""}.`;
    const move: LadderMove = { unitKey: unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets), target };
    return { outcome: { kind: "changed", sets, reason: said }, move, said, paused: false };
  }
  if (action.kind === "set") {
    // A count change on a split prescription is refused; a TABLE counts every row it writes (ramp rows included).
    const recount = action.change.table !== null ? action.change.table.length !== unit.sets.length
      : action.change.sets !== null && action.change.sets !== workingSets(unit).length;
    if (recount && unit.rowIds.length > 1) {
      const said = splitSentence(unit.name);
      return unchanged(said, said);
    }
    // 4.1b — the row's tempo · note · each side are written too (`tabsOutcome`); a set-less change keeps the sets.
    const sets = touchesSets(action.change) ? applyChange(action.change, unit.sets) : unit.sets;
    const edit = rowEdit(action, rowFacts(unit, input.day.rows));
    if (doseSignature(sets) === doseSignature(unit.sets) && edit === null) {
      return unchanged(`${unit.name}: left as written.`, null);
    }
    return { outcome: { kind: "changed", sets, reason: changedSentence(unit.name, step, action.change) }, move: null,
             said: null, paused: false };
  }
  if (action.kind === "deactivate" || action.kind === "activate") {
    // 4.1b — the row is hidden / shown next week; its sets stay as written.
    if (rowEdit(action, rowFacts(unit, input.day.rows)) === null) return unchanged(`${unit.name}: left as written.`, null);
    const said = action.kind === "deactivate" ? pausedSentence(unit.name, step) : resumedSentence(unit.name, step);
    return { outcome: { kind: "changed", sets: unit.sets, reason: said }, move: null, said: null, paused: false };
  }
  // Unreachable: a live standing tab's rule is handed to its rows by `tabsLive` before this is asked.
  return unchanged(`${unit.name}: unchanged.`, null);
}

/** Swift's `reasons.removeAll { $0 == said }` — EVERY equal sentence goes, not only the first. */
function removeAll(list: string[], said: string): void {
  for (let i = list.length - 1; i >= 0; i--) if (list[i] === said) list.splice(i, 1);
}

/* ══ F15 STEP 3.3 — A COACHED WEEK DELOADS AND SEES PAIN (mirrors `CoachDeload.swift`) ══════════════════════════════════
 * His own deload shape (`tools/deload-20261019`): the same rows; working sets ceil(half); nothing to failure; two reps fewer
 * (never below half); holds 20 % shorter (never below half); the same kilos; ramp sets, warm-up and cool-down untouched;
 * a split unit whose set count would change refused; a rounds/distance unit (`Unit.frozen`, step 3b) left as written —
 * pinned by the fixture's `f15/run-rounds#deload`. */
export const DELOAD_WEEK_SENTENCE = "Deload week: half the sets, two reps fewer, nothing to failure, same kilos.";
/** Mirrors `CoachWorkoutRules.offSentence` (F15 step 3.2). */
export const OFF_SENTENCE = "Progress automatically is off for this workout, so nothing in it progresses next week.";
/** Mirrors `CoachProgression.gateSentence` (F15 step 3.8): a main row whose rule carries §5.5's promotion gate — not read on
 *  a coach week (read, the new rung bound a second suggestion to the parent's row and the automatic path wrote nothing). */
export const gateSentence = (name: string): string =>
  `${name}: its rule's promotion gate is not read on a coach's week \u2014 it would add an exercise. `
  + "Use the workout's ladder to move on to the next exercise.";

const fewerReps = (reps: number): number => Math.max(reps - 2, Math.floor((reps + 1) / 2), 1);
const shorterHold = (s: number): number => Math.max(Math.floor(s * 0.8), Math.floor((s + 1) / 2), 1);

/** Mirrors `CoachDeload.sentence`. */
function deloadSentence(name: string, before: SetRow[], after: SetRow[]): string {
  const parts = [`${before.length} \u2192 ${after.length} sets`];
  const nums = (xs: (number | null | undefined)[]) => [...new Set(xs.filter((x): x is number => x != null))];
  const same = (a: number[], b: number[]) => a.length === b.length && a.every((x) => b.includes(x));
  const rb = nums(before.map((s) => s.reps)), ra = nums(after.map((s) => s.reps));
  if (!same(rb, ra) && rb.length > 0 && ra.length > 0) parts.push(`${Math.max(...rb)} \u2192 ${Math.max(...ra)} reps`);
  const tb = nums(before.map((s) => s.timeSeconds)), ta = nums(after.map((s) => s.timeSeconds));
  if (!same(tb, ta) && tb.length > 0 && ta.length > 0) parts.push(`${Math.max(...tb)} \u2192 ${Math.max(...ta)} s`);
  if (before.some((s) => s.setType === "failure" || s.setType === "drop_set")) parts.push("nothing to failure");
  return `${name}: deload \u2014 ${parts.join(", ")}.`;
}

/** Mirrors `CoachDeload.dose`. */
export function deloadDose(unit: Unit): Outcome {
  if (unit.sectionRole !== "main" || isRampOnly(unit) || unit.frozen === true) return { kind: "unchanged", reason: "" };
  const working = unit.sets.filter((s) => s.setType !== "warm_up");
  const keep = Math.floor((working.length + 1) / 2);
  if (keep !== working.length && unit.rowIds.length > 1) {
    return { kind: "refused", reason: `${unit.name}: the deload halves the sets, but this prescription is split across `
      + `${unit.rowIds.length} rows to keep a superset even. Re-splitting it is a decision about the pairing, so the sets `
      + "are left as they are." };
  }
  const out: SetRow[] = [];
  let workingIndex = 0;
  for (const set of unit.sets) {
    if (set.setType === "warm_up") { out.push(set); continue; }
    if (workingIndex < keep) {
      out.push({ ...set,
                 reps: set.reps == null ? set.reps : fewerReps(set.reps),
                 timeSeconds: set.timeSeconds == null ? set.timeSeconds : shorterHold(set.timeSeconds),
                 setType: set.setType === "failure" || set.setType === "drop_set" ? "regular" : set.setType });
    }
    workingIndex += 1;
  }
  const numbered = out.map((s, i) => ({ ...s, position: i }));
  if (doseSignature(numbered) === doseSignature(unit.sets)) {
    return { kind: "unchanged", reason: `${unit.name}: already as light as a deload makes it.` };
  }
  return { kind: "changed", sets: numbered,
           reason: deloadSentence(unit.name, working, numbered.filter((s) => s.setType !== "warm_up")) };
}

/** Mirrors `CoachDeload.painSentence`. */
export function painSentence(regions: string[], held: string[], deload: boolean): string {
  const named = held.join(", ");
  const middle = held.length === 0 ? "nothing progressing in this workout trains it."
    : deload ? `${named} train${held.length === 1 ? "s" : ""} it; this deload week is lighter already.`
    : `${named} held where ${held.length === 1 ? "it is" : "they are"} until it settles.`;
  return `Pain reported (${regions.join(", ")}): ${middle} If it does not settle, it is worth a professional's eyes.`;
}

/** The weekly check-in, as `plan` receives it. Mirrors `WeeklyCheckIn.Answers` at the one place
 *  `CoachProgression` reads it. */
export interface CheckIn {
  effort: string;
  pain: Record<string, { score: number; worseAfter: boolean }>;
}

/** Mirrors `Input.painfulRegions`, V6Progression.swift:396.
 *
 *  🔴 A SCORE OF **0** IS NOT PAIN. The check-in writes a report only when a region was tapped,
 *  but a tapped region with a zero score is an athlete saying IT STOPPED HURTING. Counting it
 *  would freeze every row training that region for a complaint that has been withdrawn. This is
 *  the derivation `v6rules.ts` deliberately does NOT carry — it takes `painfulRegions` directly —
 *  and this is the caller that owes it. */
export const painfulRegionsOf = (checkIn: CheckIn): string[] =>
  Object.entries(checkIn.pain).filter(([, r]) => r.score > 0).map(([region]) => region).sort();

export interface PlanInput {
  day: Day;
  logged: LoggedSession | null;
  /** Every earlier assignment of the same root workout, MOST RECENT FIRST. */
  history: PriorDay[];
  weekNumber: number;
  isDeload: boolean;
  /** F15 step 3.2 — the workout's switch is OFF (mirrors `Input.progressSwitchedOff`); absent = ON. */
  progressSwitchedOff?: boolean;
  checkIn: CheckIn;
  /** `coach_exercises.primary_muscles` by library exercise id — the THIRD pain vocabulary. */
  musclesByExerciseId: Record<string, string[]>;
  /** F15 step 3.4 — mirrors `Input.ladderTargets`: the library rows a ladder on this day names as its NEXT step, by
   *  lower-cased id. An id absent here was deleted (or is not the coach's): the chain ends there. Absent = none. */
  ladderTargets?: Record<string, LadderTarget>;
  /** §5.5 mints an id for a row it introduces. Injected, never baked in: see `v6rules.RuleInput
   *  .mint` for why the parity fixture depends on it being deterministic. */
  mint: () => string;
}

/** Mirrors `CoachProgression.plan(_:)`, CoachProgression.swift:798. */
export function plan(original: PlanInput): Plan {
  /* F15 step 4.1 — the tabs first: a standing tab's rule is handed to its rows unchanged (Swift: the same line). */
  const { tabs: tabsByKey, input } = tabsLive(original);
  const allUnits = units(input.day.rows);
  const warnings: string[] = input.progressSwitchedOff === true ? [OFF_SENTENCE] : [];

  /* ── F15 step 3.3 — a deload week is the coach's shape, never `nextWeek`'s; OFF never deloads (Swift: the same branch) ── */
  if (input.isDeload && !input.progressSwitchedOff) {
    const painful = painfulRegionsOf(input.checkIn);
    const plans: UnitPlan[] = [];
    const reasons: string[] = [];
    const touched: string[] = [];
    for (const unit of allUnits) {
      let outcome = deloadDose(unit);
      if (outcome.kind === "changed") reasons.push(outcome.reason);
      else if (outcome.kind === "refused") warnings.push(outcome.reason);
      else if (outcome.reason === "") outcome = { kind: "unchanged", reason: `${unit.name}: left as written.` };
      if (unit.sectionRole === "main" && !touched.includes(unit.name)
          && painRegions(input.musclesByExerciseId[unit.libraryExerciseId] ?? []).some((r) => painful.includes(r))) {
        touched.push(unit.name);
      }
      plans.push({ unit, outcome });
    }
    warnings.push(...orderFaults(input.day.rows).map(orderFaultMessage));
    warnings.push(...configFaults(input.day.rows));
    return {
      assignmentId: input.day.assignmentId, plans, reasons, warnings, frozenForWantOfALog: false,
      changedUnits: plans.filter((p) => p.outcome.kind === "changed").length,
      isDeloadWeek: true,
      proposals: painful.length === 0 ? [] : [painSentence(painful, touched, true)],
      pausedForPain: [], moves: [], ladderSaid: [],
    };
  }

  /* ── The log, and the ONE reason it may be discarded ── (Swift :802)
     ⚠️ A RECONSTRUCTED session is read as UNTRAINED. It was built from the prescription rather
     than logged as it happened, so every set matches by construction and no rule could ever
     disagree with what was asked for — advancing on it would be advancing on nothing. */
  let usable: LoggedSession | null = input.logged;
  if (usable !== null && isReconstructed(usable)) {
    warnings.push(`The ${dateText(usable.date)} session was reconstructed from the prescription `
      + "rather than logged as it happened, so it is read as untrained: every rule holds rather "
      + "than advancing on numbers that cannot disagree with what was asked for.");
    usable = null;
  }

  let pairedSets = new Map<string, LoggedSet[]>();
  if (usable !== null) {
    const paired = pair(input.day.rows, usable);
    if (paired !== null) {
      pairedSets = paired;
    } else {
      /* ⚠️ NOTHING IS PROGRESSED FROM A MISMATCHED LOG, because every row after a mismatch would
         name the wrong prescription and still look right. */
      warnings.push(`What was logged on ${dateText(usable.date)} does not line up with what was `
        + "assigned — a different number of exercises, or a different exercise at some position. "
        + "Nothing is progressed from it, because every row after a mismatch would name the wrong "
        + "prescription and still look right.");
    }
  }

  /* ── Build the engine's own inputs ── (Swift :825) */
  const sessionId = input.day.assignmentId;
  const rows: RuleRow[] = [];
  const logs: Record<string, { sets: ReturnType<typeof fromCoachLoggedSet>[] }> = {};
  const unitById = new Map<string, Unit>();
  const prescriptionById = new Map<string, RuleRowPrescription>();
  const ladderById = new Map<string, LadderChain>();
  const tabsById = new Map<string, TabsUnit>();
  const gateNotRead: string[] = [];

  for (const unit of allUnits) {
    const dose = workingDose(unit);
    const history = priorSessions(unit, input.history);
    const state: Record<string, Json> = {
      clean_sessions: cleanStreak(history, dose),
      clean_weeks: cleanStreak(history, dose),
      consecutive_fails: consecutiveMisses(history),
    };

    /* 🔴 A RULE APPLIES TO MAIN WORK AND NOTHING ELSE, AND THAT IS WHAT MAKES A RULE SAFE TO
       STORE ON THE LIBRARY ROW. The same library exercise legitimately appears twice in one day
       at two doses — MEASURED live, `Back Squat` is 2 × 8 @ 20 kg in the WARM-UP and 3 × 5 @ 60 kg
       in MAIN on his Wednesday — so a rule read off the exercise alone would put +2.5 kg a week
       on his ramp as well as on his work set. The section role separates them, and it is DATA
       rather than a guess. It is also what every §5 rule already says of itself: the deload halves
       MAIN sections only, the straight-arm budget sums MAIN holds only, and the wrist ramp is
       explicitly main-role because the wrist prep in the warm-up IS the rehab.
       ⚠️ AND A RAMP-ONLY UNIT IN MAIN IS UNRULED TOO: every set of it is typed `warm_up` and
       logged `warmup`, so it can never pair a working set and would read "nothing was logged"
       every week — noise that hid the real finding beside it. */
    const ruled = unit.sectionRole === "main" && !isRampOnly(unit);
    /* F15 step 3.4 — a LADDER is decided here, not by `nextWeek` (it has no such rule): the unit goes over unruled and
       `ladderOutcome` answers for it below. A frozen (rounds/distance) unit is never read as a ladder. */
    const ladder = ruled && unit.frozen !== true ? ladderChain(unit.rule) : null;
    const tabs = ruled && unit.frozen !== true
      ? tabsByKey.get(unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets)) ?? null : null;
    const byRule = ruled && ladder === null && !isTabs(unit.rule);
    if (byRule && unit.rule !== null && typeof unit.rule === "object" && !Array.isArray(unit.rule)
        && (unit.rule as Record<string, Json>)["gate"] !== undefined) gateNotRead.push(unit.name);
    const prescription = prescriptionFor(byRule ? unit : { ...unit, rule: null },
                                         sessionId, byRule ? state : {});
    prescriptionById.set(prescription.id, prescription);
    if (ladder !== null) ladderById.set(prescription.id, ladder);
    if (tabs !== null) tabsById.set(prescription.id, tabs);
    rows.push({
      id: prescription.id,
      prescription,
      facts: {
        id: unit.libraryExerciseId,
        name: unit.name,
        isStraightArm: false,
        loadsGrip: null,
        loadsPronationSupination: null,
        painRegions: painRegions(input.musclesByExerciseId[unit.libraryExerciseId] ?? []),
      },
      sectionRole: unit.sectionRole,
    });
    unitById.set(prescription.id, unit);

    const sets = unit.rowIds.flatMap((id) => pairedSets.get(id) ?? []);
    if (sets.length > 0) logs[prescription.id] = { sets: sets.map(fromCoachLoggedSet) };
  }

  const output = nextWeek({
    weekNumber: input.weekNumber,
    /* F15 step 3.3: a coach deload never reaches `nextWeek` (handled above); OFF + a deload plans as OFF. */
    isDeload: false,
    rows,
    logs,
    /* ⚠️ NO `engine_config`, AND THAT IS STATED RATHER THAN HIDDEN. `coach_workouts` has no wave
       layout and no straight-arm budget, so §4's budget reads `notConfigured` and its weekly hold
       ceiling does not run on this path. The warning below says so on every run, because a budget
       that silently does not apply reads exactly like a budget that was satisfied. */
    config: new ProgramConfig(undefined),
    painfulRegions: painfulRegionsOf(input.checkIn),
    gateAnswers: {},
    testedMaxSeconds: {},
    catalogue: {},
    mint: input.mint,
  });

  if (!output.ok) {
    /* The port refuses whole calls for anything it has not reached. It reaches everything today,
       so this cannot fire — but a caller must never read a refusal as a plan, and silently
       returning an empty one is how that would happen. */
    return {
      assignmentId: input.day.assignmentId, plans: [], reasons: [],
      warnings: [...warnings, output.message], frozenForWantOfALog: pairedSets.size === 0,
      changedUnits: 0, isDeloadWeek: false, proposals: [], pausedForPain: [], moves: [], ladderSaid: [],
    };
  }

  const reasons = output.changes.filter((c) => c.kind !== "frozen").map((c) => c.reason);
  warnings.push(...output.warnings);
  /* His catch, 2026-08-31. Reported by the READER rather than by a separate lint, so there is ONE
     statement of the rule and it runs wherever a coach week is read. */
  warnings.push(...orderFaults(input.day.rows).map(orderFaultMessage));
  warnings.push(...configFaults(input.day.rows));
  warnings.push(...gateNotRead.map(gateSentence));
  if (allUnits.some((u) => u.isHold && u.sectionRole === "main")) {
    warnings.push("\u00a74's weekly straight-arm hold budget does not run on a coach-assigned week: "
      + "the budget lives in a program's `engine_config` and a coach workout has none. Main-block "
      + "holds are progressed here without it.");
  }

  const plans: UnitPlan[] = [];
  const moves: LadderMove[] = [];
  const rowEdits: RowEditAt[] = [];
  const ladderMoved: string[] = [], ladderEnded: string[] = [], ladderPaused: string[] = [];
  const painful = painfulRegionsOf(input.checkIn);
  for (const planned of output.rows) {
    const unit = unitById.get(planned.sourceRowId);
    if (unit === undefined) continue;
    const said = output.changes.find((c) => c.rowId === planned.sourceRowId
                                            && c.kind !== "frozen")?.reason;
    /* W136 B2 S3 step 3b — measured in rounds or distance: FROZEN WHOLE, whatever the engine answered. */
    if (unit.frozen === true) {
      if (said !== undefined) removeAll(reasons, said);
      plans.push({ unit, outcome: { kind: "unchanged",
                                    reason: `${unit.name}: measured in rounds or distance \u2014 left as written.` } });
      continue;
    }
    /* F15 step 3.4 — a ladder unit: its outcome is the ladder's, whatever the unruled pass said. */
    const chain = ladderById.get(planned.sourceRowId), tabs = tabsById.get(planned.sourceRowId);
    if (chain !== undefined || tabs !== undefined) {
      if (said !== undefined) removeAll(reasons, said);
      const current = unit.rowIds.flatMap((id) => pairedSets.get(id) ?? []);
      let decided: { outcome: Outcome; move: LadderMove | null; said: string | null; paused: boolean };
      if (chain !== undefined) decided = ladderOutcome(unit, chain, current, input, painful);
      else {
        const t = tabsOutcome(unit, tabs!, current, input, painful);
        decided = t;
        if (t.edit !== null) rowEdits.push({ unitKey: unitKey(unit.libraryExerciseId, unit.sectionRole, unit.sets), edit: t.edit });
      }
      if (decided.move !== null) { moves.push(decided.move); ladderMoved.push(decided.said ?? ""); }
      else if (decided.said !== null) ladderEnded.push(decided.said);
      if (decided.paused) ladderPaused.push(unit.name);
      if (decided.outcome.kind === "changed") reasons.push(decided.outcome.reason);
      plans.push({ unit, outcome: decided.outcome });
      continue;
    }
    let outcome = redistribute(planned.prescription, unit);

    if (outcome.kind === "changed") {
      outcome = { kind: "changed", sets: outcome.sets,
                  reason: said ?? `${unit.name}: the prescription moved.` };
    } else if (outcome.kind === "unchanged") {
      const before = prescriptionById.get(planned.sourceRowId);
      if (said !== undefined && before !== undefined
          && doseMoved(before, planned.prescription)) {
        /* 🔴 THE RULE MOVED THE PRESCRIPTION AND THE ROWS DID NOT. Its sentence was about to reach
           the coach as an APPLIED change — "+2.5 kg to 2.5 kg" on a row whose sets carry no weight
           at all is the live shape. The sentence is DROPPED from `reasons` and a warning naming
           the discrepancy stands in its place, so what the coach reads is what was written. */
        removeAll(reasons, said);
        warnings.push(`${unit.name}: the rule decided \u00ab${said}\u00bb but the change could not be `
          + "written onto this row's sets, so it was DROPPED rather than applied. If it is wanted, "
          + "make it by hand.");
        outcome = { kind: "unchanged",
                    reason: `${unit.name}: the rule's change could not be written onto the sets.` };
      } else {
        outcome = {
          kind: "unchanged",
          reason: output.changes.find((c) => c.rowId === planned.sourceRowId)?.reason
                  ?? `${unit.name}: unchanged.`,
        };
      }
    } else {
      /* `refused` — `redistribute` will not guess which side of a SPLIT prescription a gained or
         lost set belongs to. The sentence is dropped and the refusal becomes a warning. */
      if (said !== undefined) removeAll(reasons, said);
      warnings.push(outcome.reason);
    }
    plans.push({ unit, outcome });
  }

  /* ── F15 step 3.3 — §5.12's stops reach the coach as PROPOSALS (never applied), pain named by what it holds ── */
  const pausedForPain = plans.filter((p) => p.unit.sectionRole === "main" && p.unit.rule !== null && p.unit.rule !== undefined
      && output.changes.some((c) => c.kind === "painFreeze" && unitById.get(c.rowId) === p.unit))
    .map((p) => p.unit.name).concat(ladderPaused);
  const proposals = output.stops.map((stop) => stop.kind === "painAlert"
    ? painSentence(painfulRegionsOf(input.checkIn), pausedForPain, false) : stop.message);

  return {
    assignmentId: input.day.assignmentId,
    plans,
    reasons,
    warnings,
    /* ⚠️ "nothing to progress from", not "nothing changed". */
    frozenForWantOfALog: pairedSets.size === 0,
    changedUnits: plans.filter((p) => p.outcome.kind === "changed").length,
    isDeloadWeek: false,
    proposals,
    pausedForPain,
    moves,
    ladderSaid: [...ladderMoved, ...ladderEnded],
    rowEdits,
  };
}
